import { AppointmentsHost } from './Appointments';
import { AuctionsHost } from './Auctions';
import {tx, ptx, usePageText} from '../../lib/page-text';
import {ConferenceHost} from './Conference';
import {NetworkingHost} from './Networking';
import { SportsControl } from './SportsBoard';
import { AudienceReport } from './AudienceReport';
import {LearningWorkspace} from "./LearningWorkspace";
import { Link } from "../../lib/localized-router";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api-client";
import { useAuth } from "../../lib/auth-store";
import type { Program } from "../../../../api/src/live-program/program-schema";
const failure = (e: unknown) => {
  const x = e as { response?: { data?: { message?: string } } };
  return x.response?.data?.message ?? "Could not save. Please try again.";
};
export const FEATURES: [keyof Program["features"], string, string][] = [
  [
    "registration",
    "Registration",
    "Short forms, approval and waitlist. This does not replace the stream’s viewing access settings.",
  ],
  [
    "attendance",
    "Attendance",
    "Playback heartbeats and check-in. Device activity is not proof of attention.",
  ],
  ["teaching", "Quizzes", "Scored questions with one submission per attendee."],
  ["materials", "Materials", "A resource shelf beside the player."],
  [
    "actions",
    "Action cards",
    "Show one call to action without covering the presentation.",
  ],
  ["agenda", "Agenda & series", "Schedule sessions and speakers."],
  [
    "bookings",
    "Appointments",
    "Limited-capacity appointment slots for approved registrants.",
  ],
  [
    "certificates",
    "Certificates",
    "Completion based on recorded watch time and quiz scores.",
  ],
  [
    "timeline",
    "Presentation replays",
    "Capture board changes, layout switches and chapter marks from now on.",
  ],
  ["production", "Production cues", "A run of show and session timer."],
  [
    "followup",
    "Follow-up",
    "Segment attendees, export messages and trigger configured webhooks.",
  ],
];
const PACKS: Record<string, (keyof Program["features"])[]> = {
  Business: [
    "registration",
    "attendance",
    "actions",
    "materials",
    "timeline",
    "followup",
    "production",
  ],
  Education: [
    "registration",
    "attendance",
    "teaching",
    "materials",
    "certificates",
    "timeline",
    "production",
  ],
  Commerce: ["attendance", "actions", "followup", "production"],
  Consultation: ["registration", "attendance", "bookings", "materials"],
  Conference: [
    "registration",
    "attendance",
    "agenda",
    "materials",
    "timeline",
    "production",
  ],
  Creator: ["attendance", "actions", "agenda", "timeline", "production"],
};
const uid = () => crypto.randomUUID();
const dateInput = (v: string) => {
  const d = new Date(v);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};
const dateValue = (v: string) =>
  v ? new Date(v).toISOString() : new Date().toISOString();
export function ProgramWorkspace({
  stream,
  mode = "setup",
  privateRoomBlocked = false,
}: {
  stream: { id: string; status: string };
  mode?: "setup" | "control" | "report";
  privateRoomBlocked?: boolean;
}) {
  usePageText();
  const qc = useQueryClient(),
    canEdit =
      useAuth((s) =>
        ["OWNER", "ADMIN", "MEMBER"].includes(s.user?.role ?? ""),
      ) && stream.status !== "ARCHIVED";
  const prefix = `/me/streams/${stream.id}/program`;
  const role = useAuth((s) => s.user?.role);
  const streams = useQuery({
    queryKey: ["program-streams"],
    enabled: mode === "setup",
    queryFn: async () =>
      (await api.get<{ id: string; name: string }[]>("/me/streams")).data,
  });
  const team = useQuery({
    queryKey: ["program-team", stream.id],
    enabled: mode === "setup",
    queryFn: async () =>
      (
        await api.get<
          { id: string; name: string; email: string; role: string }[]
        >(prefix + "/team")
      ).data,
  });
  const query = useQuery({
    queryKey: ["program", stream.id],
    queryFn: async () =>
      (await api.get<{ version: number; config: Program; permissions?: { produce: boolean } }>(prefix)).data,
  });
  const automation = useQuery({
    queryKey: ["program-automation", stream.id],
    enabled: mode === "setup" && !!query.data?.config.features.followup,
    queryFn: async () =>
      (
        await api.get<{
          reminderConnected: boolean;
          followupConnected: boolean;
          queued: number;
          delivered: number;
          failed: number;
        }>(prefix + "/automation-status")
      ).data,
    refetchInterval: 30000,
  });
  const [draft, setDraft] = useState<{
      version: number;
      config: Program;
    } | null>(null),
    [dirty, setDirty] = useState(false),
    [busy, setBusy] = useState(false),
    [notice, setNotice] = useState(""),
    [chapter, setChapter] = useState(""),
    [timer, setTimer] = useState<number | null>(null),
    [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (query.data && !dirty) setDraft(query.data);
  }, [query.data, dirty]);
  useEffect(() => {
    if (!timer) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [timer]);
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  const change = (config: Program) => {
    setDraft((d) => (d ? { ...d, config } : d));
    setDirty(true);
    setNotice("");
  };
  const save = async (config = draft?.config) => {
    if (!draft || !config) return;
    setBusy(true);
    setNotice("");
    try {
      const { data } = await api.patch(prefix, {
        version: draft.version,
        config,
      });
      setDraft(data);
      setDirty(false);
      qc.setQueryData(["program", stream.id], data);
      setNotice("Saved");
    } catch (e) {
      setNotice(failure(e));
    } finally {
      setBusy(false);
    }
  };
  if (query.isPending) return <p role="status">{tx("Loading event tools…")}</p>;
  if (query.isError || !draft)
    return (
      <div className="card" role="alert">{tx("Event tools could not load.")}{" "}
        <button className="btn outline" onClick={() => void query.refetch()}>{tx("Retry")}</button>
      </div>
    );
  const p = draft.config,
    disabled = !canEdit || busy;
  const list = <
    K extends "quizzes" | "cards" | "materials" | "agenda" | "slots" | "cues",
  >(
    key: K,
    index: number,
    value: Partial<Program[K][number]>,
  ) =>
    change({
      ...p,
      [key]: p[key].map((x, i) => (i === index ? { ...x, ...value } : x)),
    });
  const remove = (
    key: "quizzes" | "cards" | "materials" | "agenda" | "slots" | "cues",
    index: number,
  ) =>
    change({
      ...p,
      [key]: p[key].filter((_, i) => i !== index),
      ...(key === "cards" && p.cards[index].id === p.activeCardId
        ? { activeCardId: null }
        : {}),
    });
  if (mode === "report")
    return <><LearningWorkspace streamId={stream.id} program={p} report/><AudienceReport streamId={stream.id} config={p} canEdit={canEdit} /></>;
  return (
    <section className="card live-program">
      <SportsControl streamId={stream.id}/>
      <NetworkingHost streamId={stream.id}/>
      <AppointmentsHost key={stream.id} streamId={stream.id} privateRoomBlocked={privateRoomBlocked}/>
      <AuctionsHost key={stream.id} streamId={stream.id}/>
      <ConferenceHost key={stream.id} streamId={stream.id}/>
      {(canEdit || query.data?.permissions?.produce) && <LearningWorkspace streamId={stream.id} program={p} allowReuse={!dirty&&!busy}/>}
      <details open={mode === "control" ? true : undefined}>
        <summary>{ptx("{v0} ·{v1}{v2} enabled", {v0: (mode === "setup" ? tx("Optional event tools") : tx("Configure live tools")), v1: (" "), v2: (Object.values(p.features).filter(Boolean).length)})}</summary>
        <div className="row-between">
          <div>
            <h3>{mode === "setup" ? tx("Event tools") : tx("Live event controls")}</h3>
            <p className="muted">
              {mode === "setup"
                ? tx("Choose a setup or switch on just the tools you need. Expand a tool to configure it.")
                : tx("Manage the tools enabled for this event. Changes appear beside the viewer’s player.")}
            </p>
          </div>
          {dirty && <span className="badge">{tx("Unsaved changes")}</span>}
        </div>
        {mode === "setup" && (
          <>
            <div
              className="program-packs"
              aria-label={tx("Recommended feature packs")}
            >
              {Object.keys(PACKS).map((pack) => (
                <button
                  key={tx(pack)}
                  className="btn outline sm"
                  disabled={disabled}
                  onClick={() =>
                    change({
                      ...p,
                      features: Object.fromEntries(
                        FEATURES.map(([key]) => [
                          key,
                          PACKS[pack].includes(key),
                        ]),
                      ) as Program["features"],
                    })
                  }
                >
                  {tx(pack)}
                </button>
              ))}
            </div>
            <div className="program-switches">
              {FEATURES.map(([key, label, help]) => (
                <label key={key}>
                  <input
                    type="checkbox"
                    checked={p.features[key]}
                    disabled={disabled}
                    onChange={(e) =>
                      change({
                        ...p,
                        features: {
                          ...p.features,
                          [key]: e.target.checked,
                          ...(e.target.checked && key === "certificates"
                            ? { attendance: true, registration: true }
                            : {}),
                          ...(e.target.checked && key === "bookings"
                            ? { registration: true }
                            : {}),
                          ...(!e.target.checked && key === "registration"
                            ? { bookings: false, certificates: false }
                            : {}),
                          ...(!e.target.checked && key === "attendance"
                            ? { certificates: false }
                            : {}),
                        },
                      })
                    }
                  />
                  <span>
                    <strong>{tx(label)}</strong>
                    <small>{tx(help)}</small>
                  </span>
                </label>
              ))}
            </div>
          </>
        )}
        {mode === "setup" && (
          <details>
            <summary>{tx("Stream team")}</summary>
            <p className="muted">{tx("Assign existing workspace members. A Viewer can receive stream-specific teaching, production or question-moderation access without changing their workspace role.")}</p>
            {team.data?.map((m) => {
              const assigned = p.team.find((x) => x.userId === m.id);
              return (
                <label key={m.id}>
                  {m.name || m.email} · {m.role}
                  <select
                    className="input"
                    value={assigned?.role ?? ""}
                    disabled={
                      disabled || !["OWNER", "ADMIN"].includes(role ?? "")
                    }
                    onChange={(e) =>
                      change({
                        ...p,
                        team: [
                          ...p.team.filter((x) => x.userId !== m.id),
                          ...(e.target.value
                            ? [
                                {
                                  userId: m.id,
                                  role: e.target
                                    .value as Program["team"][number]["role"],
                                },
                              ]
                            : []),
                        ],
                      })
                    }
                  >
                    <option value="">{tx("No additional assignment")}</option>
                    <option value="producer">{tx("Producer · board and event controls")}</option>
                    <option value="teacher">{tx("Teacher · board, quizzes and questions")}</option>
                    <option value="moderator">{tx("Moderator · questions")}</option>
                  </select>
                </label>
              );
            })}
            {team.isError && (
              <button
                className="btn outline"
                onClick={() => void team.refetch()}
              >{tx("Retry team")}</button>
            )}
          </details>
        )}
        <fieldset disabled={disabled} className="program-fields">
          {mode === "setup" && p.features.registration && (
            <details>
              <summary>{tx("Registration settings")}</summary>
              <div className="program-row">
                <label>{ptx("Approved attendee capacity{v0}", {v0: (<input
                    className="input"
                    type="number"
                    min={1}
                    max={100000}
                    value={p.registration.capacity}
                    onChange={(e) =>
                      change({
                        ...p,
                        registration: {
                          ...p.registration,
                          capacity: Number(e.target.value),
                        },
                      })
                    }
                  />)})}</label>
                <label>{ptx("{v0}{v1}Approve registrations manually", {v0: (<input
                    type="checkbox"
                    checked={p.registration.approval}
                    onChange={(e) =>
                      change({
                        ...p,
                        registration: {
                          ...p.registration,
                          approval: e.target.checked,
                        },
                      })
                    }
                  />), v1: (" ")})}</label>
              </div>
              <p className="muted">{tx("Name, email and consent are included. Email addresses are self-reported; these forms are not identity verification.")}</p>
              {p.registration.fields.map((f, i) => (
                <div key={f.id} className="program-row">
                  <label>{ptx("Question {v0}{v1}", {v0: (i + 1), v1: (<input
                      className="input"
                      value={f.label}
                      maxLength={180}
                      onChange={(e) =>
                        change({
                          ...p,
                          registration: {
                            ...p.registration,
                            fields: p.registration.fields.map((x, n) =>
                              n === i ? { ...x, label: e.target.value } : x,
                            ),
                          },
                        })
                      }
                    />)})}</label>
                  <label>{ptx("{v0}Required", {v0: (<input
                      type="checkbox"
                      checked={f.required}
                      onChange={(e) =>
                        change({
                          ...p,
                          registration: {
                            ...p.registration,
                            fields: p.registration.fields.map((x, n) =>
                              n === i
                                ? { ...x, required: e.target.checked }
                                : x,
                            ),
                          },
                        })
                      }
                    />)})}</label>
                  <button
                    className="btn ghost sm"
                    onClick={() =>
                      change({
                        ...p,
                        registration: {
                          ...p.registration,
                          fields: p.registration.fields.filter(
                            (_, n) => n !== i,
                          ),
                        },
                      })
                    }
                  >{tx("Remove")}</button>
                </div>
              ))}
              <button
                className="btn outline sm"
                disabled={disabled || p.registration.fields.length >= 10}
                onClick={() =>
                  change({
                    ...p,
                    registration: {
                      ...p.registration,
                      fields: [
                        ...p.registration.fields,
                        { id: uid(), label: "Company", required: false },
                      ],
                    },
                  })
                }
              >{tx("Add question")}</button>
            </details>
          )}
          {p.features.teaching && (
            <details>
              <summary>{ptx("Quizzes · {v0}", {v0: (p.quizzes.length)})}</summary>
              {p.quizzes.map((q, i) => (
                <article className="program-item" key={q.id}>
                  <label>{ptx("Question{v0}", {v0: (<input
                      className="input"
                      value={q.title}
                      onChange={(e) =>
                        list("quizzes", i, { title: e.target.value })
                      }
                    />)})}</label>
                  {q.options.map((o, j) => (
                    <label key={j}>{ptx("Answer {v0}{v1}", {v0: (j + 1), v1: (<input
                        className="input"
                        value={o}
                        onChange={(e) =>
                          list("quizzes", i, {
                            options: q.options.map((x, n) =>
                              n === j ? e.target.value : x,
                            ),
                          })
                        }
                      />)})}</label>
                  ))}
                  <div className="program-row">
                    <label>{ptx("Correct answer{v0}", {v0: (<select
                        className="input"
                        value={q.answer}
                        onChange={(e) =>
                          list("quizzes", i, { answer: Number(e.target.value) })
                        }
                      >
                        {q.options.map((_, j) => (
                          <option key={j} value={j}>{ptx("Answer {v0}", {v0: (j + 1)})}</option>
                        ))}
                      </select>)})}</label>
                    <label>{ptx("{v0}Open to viewers", {v0: (<input
                        type="checkbox"
                        checked={q.open}
                        onChange={(e) =>
                          list("quizzes", i, { open: e.target.checked })
                        }
                      />)})}</label>
                    <button
                      className="btn ghost sm"
                      onClick={() => remove("quizzes", i)}
                    >{tx("Remove quiz")}</button>
                  </div>
                </article>
              ))}
              <button
                className="btn outline sm"
                disabled={p.quizzes.length >= 50}
                onClick={() =>
                  change({
                    ...p,
                    quizzes: [
                      ...p.quizzes,
                      {
                        id: uid(),
                        title: "New question",
                        options: ["First answer", "Second answer"],
                        answer: 0,
                        open: false,
                      },
                    ],
                  })
                }
              >{tx("Add quiz")}</button>
              <p className="muted">{tx("Save to publish. Questions and answers cannot be edited after a submission; close the quiz and create another.")}</p>
            </details>
          )}
          {p.features.actions && (
            <details>
              <summary>{ptx("Action cards · {v0}", {v0: (p.cards.length)})}</summary>
              {p.cards.map((c, i) => (
                <article className="program-item" key={c.id}>
                  <div className="program-row">
                    <label>{ptx("Title{v0}", {v0: (<input
                        className="input"
                        value={c.title}
                        onChange={(e) =>
                          list("cards", i, { title: e.target.value })
                        }
                      />)})}</label>
                    <label>{ptx("Purpose{v0}", {v0: (<select
                        className="input"
                        value={c.kind}
                        onChange={(e) =>
                          list("cards", i, {
                            kind: e.target.value as typeof c.kind,
                          })
                        }
                      >
                        {[
                          "resource",
                          "demo",
                          "booking",
                          "survey",
                          "product",
                        ].map((k) => (
                          <option key={k}>{k}</option>
                        ))}
                      </select>)})}</label>
                  </div>
                  <label>{ptx("Description{v0}", {v0: (<textarea
                      className="input"
                      value={c.body}
                      onChange={(e) =>
                        list("cards", i, { body: e.target.value })
                      }
                    />)})}</label>
                  <div className="program-row">
                    <label>{ptx("Button label{v0}", {v0: (<input
                        className="input"
                        value={c.label}
                        onChange={(e) =>
                          list("cards", i, { label: e.target.value })
                        }
                      />)})}</label>
                    <label>{ptx("HTTPS destination{v0}", {v0: (<input
                        className="input"
                        type="url"
                        value={c.url}
                        onChange={(e) =>
                          list("cards", i, { url: e.target.value })
                        }
                      />)})}</label>
                  </div>
                  <div className="program-row">
                    <button
                      className="btn outline sm"
                      onClick={() =>
                        change({
                          ...p,
                          activeCardId: p.activeCardId === c.id ? null : c.id,
                        })
                      }
                    >
                      {p.activeCardId === c.id ? tx("Hide card") : tx("Show this card")}
                    </button>
                    <button
                      className="btn ghost sm"
                      onClick={() => remove("cards", i)}
                    >{tx("Remove")}</button>
                  </div>
                </article>
              ))}
              <button
                className="btn outline sm"
                disabled={p.cards.length >= 30}
                onClick={() =>
                  change({
                    ...p,
                    cards: [
                      ...p.cards,
                      {
                        id: uid(),
                        title: "Request a demo",
                        body: "",
                        label: "Learn more",
                        url: "",
                        kind: "demo",
                      },
                    ],
                  })
                }
              >{tx("Add action card")}</button>
            </details>
          )}
          {p.features.materials && (
            <details>
              <summary>{ptx("Materials · {v0}", {v0: (p.materials.length)})}</summary>
              <label className="program-upload">{ptx("Add a PDF · up to 5 MB{v0}", {v0: (<input
                  type="file"
                  accept="application/pdf"
                  disabled={disabled || p.materials.length >= 50}
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (!f) return;
                    setBusy(true);
                    try {
                      const form = new FormData();
                      form.append("file", f);
                      const { data } = await api.post(prefix + "/files", form);
                      change({
                        ...p,
                        materials: [
                          ...p.materials,
                          { id: data.id, title: data.title, url: data.url },
                        ],
                      });
                    } catch (e) {
                      setNotice(failure(e));
                    } finally {
                      setBusy(false);
                    }
                  }}
                />)})}</label>
              {p.materials.map((m, i) => (
                <div className="program-item" key={m.id}>
                  <label>{ptx("Resource title{v0}", {v0: (<input
                      className="input"
                      value={m.title}
                      onChange={(e) =>
                        list("materials", i, { title: e.target.value })
                      }
                    />)})}</label>
                  <label>{ptx("HTTPS resource link{v0}", {v0: (<input
                      className="input"
                      type="url"
                      value={m.url}
                      onChange={(e) =>
                        list("materials", i, { url: e.target.value })
                      }
                    />)})}</label>
                  <button
                    className="btn ghost sm"
                    onClick={() => remove("materials", i)}
                  >{tx("Remove")}</button>
                </div>
              ))}
              <button
                className="btn outline sm"
                disabled={p.materials.length >= 50}
                onClick={() =>
                  change({
                    ...p,
                    materials: [
                      ...p.materials,
                      { id: uid(), title: "Lesson materials", url: "" },
                    ],
                  })
                }
              >{tx("Add resource")}</button>
            </details>
          )}
          {mode === "setup" && p.features.agenda && (
            <details>
              <summary>{ptx("Agenda & recurring sessions · {v0}", {v0: (p.agenda.length)})}</summary>
              {p.agenda.map((a, i) => (
                <article className="program-item" key={a.id}>
                  <label>{ptx("Session title{v0}", {v0: (<input
                      className="input"
                      value={a.title}
                      onChange={(e) =>
                        list("agenda", i, { title: e.target.value })
                      }
                    />)})}</label>
                  <div className="program-row">
                    <label>{ptx("Start (your timezone){v0}", {v0: (<input
                        type="datetime-local"
                        className="input"
                        value={dateInput(a.startsAt)}
                        onChange={(e) =>
                          list("agenda", i, {
                            startsAt: dateValue(e.target.value),
                          })
                        }
                      />)})}</label>
                    <label>{ptx("Minutes{v0}", {v0: (<input
                        className="input"
                        type="number"
                        min={1}
                        value={a.minutes}
                        onChange={(e) =>
                          list("agenda", i, { minutes: Number(e.target.value) })
                        }
                      />)})}</label>
                  </div>
                  <label>{ptx("Open a stream from this session{v0}", {v0: (<select
                      className="input"
                      value={a.streamId ?? ""}
                      onChange={(e) =>
                        list("agenda", i, {
                          streamId: e.target.value || undefined,
                        })
                      }
                    >
                      <option value="">{tx("Agenda entry only")}</option>
                      {streams.data?.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>)})}</label>
                  <label>{ptx("Speaker / sponsor{v0}", {v0: (<input
                      className="input"
                      value={a.speaker}
                      onChange={(e) =>
                        list("agenda", i, { speaker: e.target.value })
                      }
                    />)})}</label>
                  <button
                    className="btn ghost sm"
                    onClick={() =>
                      change({
                        ...p,
                        agenda: [
                          ...p.agenda,
                          {
                            ...a,
                            id: uid(),
                            startsAt: new Date(
                              Date.parse(a.startsAt) + 7 * 86400000,
                            ).toISOString(),
                          },
                        ],
                      })
                    }
                  >{tx("Repeat next week")}</button>
                  <button
                    className="btn ghost sm"
                    onClick={() => remove("agenda", i)}
                  >{tx("Remove")}</button>
                </article>
              ))}
              <button
                className="btn outline sm"
                disabled={p.agenda.length >= 100}
                onClick={() =>
                  change({
                    ...p,
                    agenda: [
                      ...p.agenda,
                      {
                        id: uid(),
                        title: "Opening session",
                        startsAt: new Date(Date.now() + 86400000).toISOString(),
                        minutes: 60,
                        speaker: "",
                      },
                    ],
                  })
                }
              >{tx("Add session")}</button>
            </details>
          )}
          {mode === "setup" && p.features.bookings && (
            <details>
              <summary>{ptx("Appointment slots · {v0}", {v0: (p.slots.length)})}</summary>
              <p className="muted">{tx("Reservations hold a place. Payment and calendar-provider synchronization are separate.")}</p>
              {p.slots.map((s, i) => (
                <article className="program-item" key={s.id}>
                  <label>{ptx("Service / appointment{v0}", {v0: (<input
                      className="input"
                      value={s.title}
                      onChange={(e) =>
                        list("slots", i, { title: e.target.value })
                      }
                    />)})}</label>
                  <div className="program-row">
                    <label>{ptx("Start (your timezone){v0}", {v0: (<input
                        className="input"
                        type="datetime-local"
                        value={dateInput(s.startsAt)}
                        onChange={(e) =>
                          list("slots", i, {
                            startsAt: dateValue(e.target.value),
                          })
                        }
                      />)})}</label>
                    <label>{ptx("Minutes{v0}", {v0: (<input
                        className="input"
                        type="number"
                        min={5}
                        value={s.minutes}
                        onChange={(e) =>
                          list("slots", i, { minutes: Number(e.target.value) })
                        }
                      />)})}</label>
                    <label>{ptx("Places{v0}", {v0: (<input
                        className="input"
                        type="number"
                        min={1}
                        max={100}
                        value={s.capacity}
                        onChange={(e) =>
                          list("slots", i, { capacity: Number(e.target.value) })
                        }
                      />)})}</label>
                  </div>
                  <button
                    className="btn ghost sm"
                    onClick={() => remove("slots", i)}
                  >{tx("Remove slot")}</button>
                </article>
              ))}
              <button
                className="btn outline sm"
                disabled={p.slots.length >= 100}
                onClick={() =>
                  change({
                    ...p,
                    slots: [
                      ...p.slots,
                      {
                        id: uid(),
                        title: "Consultation",
                        startsAt: new Date(Date.now() + 86400000).toISOString(),
                        minutes: 30,
                        capacity: 1,
                      },
                    ],
                  })
                }
              >{tx("Add appointment")}</button>
            </details>
          )}
          {mode === "setup" && p.features.certificates && (
            <details>
              <summary>{tx("Completion rules")}</summary>
              <label>{ptx("Certificate title{v0}", {v0: (<input
                  className="input"
                  value={p.completion.title}
                  onChange={(e) =>
                    change({
                      ...p,
                      completion: { ...p.completion, title: e.target.value },
                    })
                  }
                />)})}</label>
              <div className="program-row">
                <label>{ptx("Minimum minutes watched{v0}", {v0: (<input
                    className="input"
                    type="number"
                    min={0}
                    value={p.completion.minutes}
                    onChange={(e) =>
                      change({
                        ...p,
                        completion: {
                          ...p.completion,
                          minutes: Number(e.target.value),
                        },
                      })
                    }
                  />)})}</label>
                <label>{ptx("Minimum quiz score (%){v0}", {v0: (<input
                    className="input"
                    type="number"
                    min={0}
                    max={100}
                    value={p.completion.score}
                    onChange={(e) =>
                      change({
                        ...p,
                        completion: {
                          ...p.completion,
                          score: Number(e.target.value),
                        },
                      })
                    }
                  />)})}</label>
              </div>
              <p className="muted">{tx("All configured quizzes count. Unanswered questions score zero. No quizzes means attendance-only completion. Certificates show the attendee’s self-reported name. Viewers see their recorded watch time, current quiz average and remaining requirements beside the player. Course lesson completion is separate and does not automatically issue a certificate. Closed quizzes still count; open required quizzes so learners can answer them.")}</p>
            </details>
          )}
          {p.features.production && (
            <details>
              <summary>{ptx("Run of show · {v0}", {v0: (p.cues.length)})}</summary>
              {p.cues.map((c, i) => (
                <article className="program-item" key={c.id}>
                  <label>{ptx("{v0}Completed", {v0: (<input
                      type="checkbox"
                      checked={c.done}
                      onChange={(e) =>
                        list("cues", i, { done: e.target.checked })
                      }
                    />)})}</label>
                  <label>{ptx("Cue{v0}", {v0: (<input
                      className="input"
                      value={c.title}
                      onChange={(e) =>
                        list("cues", i, { title: e.target.value })
                      }
                    />)})}</label>
                  <label>{ptx("Presenter notes{v0}", {v0: (<textarea
                      className="input"
                      value={c.notes}
                      onChange={(e) =>
                        list("cues", i, { notes: e.target.value })
                      }
                    />)})}</label>
                  <div className="program-row">
                    <label>{ptx("Minutes{v0}", {v0: (<input
                        className="input"
                        type="number"
                        min={0}
                        value={c.minutes}
                        onChange={(e) =>
                          list("cues", i, { minutes: Number(e.target.value) })
                        }
                      />)})}</label>
                    <button
                      className="btn outline sm"
                      onClick={() => {
                        setNow(Date.now());
                        setTimer(Date.now() + c.minutes * 60000);
                      }}
                    >{tx("Start timer")}</button>
                    <button
                      className="btn ghost sm"
                      onClick={() => remove("cues", i)}
                    >{tx("Remove")}</button>
                  </div>
                </article>
              ))}
              <button
                className="btn outline sm"
                disabled={p.cues.length >= 100}
                onClick={() =>
                  change({
                    ...p,
                    cues: [
                      ...p.cues,
                      {
                        id: uid(),
                        title: "Welcome",
                        notes: "",
                        minutes: 5,
                        done: false,
                      },
                    ],
                  })
                }
              >{tx("Add cue")}</button>
              {timer && (
                <p role="timer">{ptx("{v0} seconds remaining{v1}{v2}", {v0: (Math.max(0, Math.ceil((timer - now) / 1000))), v1: (" "), v2: (<button
                    className="btn ghost sm"
                    onClick={() => setTimer(null)}
                  >{tx("Stop timer")}</button>)})}</p>
              )}
            </details>
          )}
          {mode === "setup" && p.features.followup && (
            <details>
              <summary>{tx("Follow-up message & schedule")}</summary>
              <p className="muted">{ptx("Scheduled actions notify your existing messaging integration, which sends the email or SMS. Connect{v0}{v1} and{v2}{v3} in{v4}{v5}.", {v0: (" "), v1: (<strong>{tx("live.reminder")}</strong>), v2: (" "), v3: (<strong>{tx("live.followup")}</strong>), v4: (" "), v5: (<Link to="/webhooks">{tx("Developer → Webhooks")}</Link>)})}</p>
              <label>{ptx("Reminder before the stream{v0}", {v0: (<select
                  className="input"
                  value={p.followup.scheduling.reminderMinutes}
                  onChange={(e) =>
                    change({
                      ...p,
                      followup: {
                        ...p.followup,
                        scheduling: {
                          ...p.followup.scheduling,
                          reminderMinutes: Number(e.target.value) as
                            | 0
                            | 15
                            | 30
                            | 60
                            | 1440,
                        },
                      },
                    })
                  }
                >
                  {[
                    [0, "Off"],
                    [15, "15 minutes"],
                    [30, "30 minutes"],
                    [60, "1 hour"],
                    [1440, "1 day"],
                  ].map(([v, label]) => (
                    <option key={v} value={v}>
                      {label}
                    </option>
                  ))}
                </select>)})}</label>
              <label className="program-consent">{ptx("{v0}Schedule follow-up after the broadcast ends", {v0: (<input
                  type="checkbox"
                  checked={p.followup.scheduling.afterEvent}
                  onChange={(e) =>
                    change({
                      ...p,
                      followup: {
                        ...p.followup,
                        scheduling: {
                          ...p.followup.scheduling,
                          afterEvent: e.target.checked,
                        },
                      },
                    })
                  }
                />)})}</label>
              {p.followup.scheduling.afterEvent && (
                <div className="program-row">
                  <label>{ptx("Send after{v0}", {v0: (<select
                      className="input"
                      value={p.followup.scheduling.afterMinutes}
                      onChange={(e) =>
                        change({
                          ...p,
                          followup: {
                            ...p.followup,
                            scheduling: {
                              ...p.followup.scheduling,
                              afterMinutes: Number(e.target.value) as
                                | 5
                                | 15
                                | 60
                                | 1440,
                            },
                          },
                        })
                      }
                    >
                      {[
                        [5, "5 minutes"],
                        [15, "15 minutes"],
                        [60, "1 hour"],
                        [1440, "1 day"],
                      ].map(([v, label]) => (
                        <option key={v} value={v}>
                          {label}
                        </option>
                      ))}
                    </select>)})}</label>
                  <label>{ptx("Follow-up audience{v0}", {v0: (<select
                      className="input"
                      value={p.followup.scheduling.audience}
                      onChange={(e) =>
                        change({
                          ...p,
                          followup: {
                            ...p.followup,
                            scheduling: {
                              ...p.followup.scheduling,
                              audience: e.target.value as
                                | "registered"
                                | "attended"
                                | "missed",
                            },
                          },
                        })
                      }
                    >
                      <option value="registered">{tx("Approved registrants")}</option>
                      <option value="attended">{tx("Registrants who watched")}</option>
                      <option value="missed">{tx("Registrants who did not watch")}</option>
                    </select>)})}</label>
                </div>
              )}
              {automation.data && (
                <p role="status">{ptx("Reminder integration:{v0}{v1}{v2}· Follow-up integration:{v3}{v4}. {v5} events queued ·{v6}{v7} deliveries accepted ·{v8}{v9} failed or cancelled.", {v0: (" "), v1: (automation.data.reminderConnected
                    ? tx("Connected")
                    : tx("Not connected")), v2: (" "), v3: (" "), v4: (automation.data.followupConnected
                    ? tx("Connected")
                    : tx("Not connected")), v5: (automation.data.queued), v6: (" "), v7: (automation.data.delivered), v8: (" "), v9: (automation.data.failed)})}</p>
              )}
              {automation.isError && (
                <button
                  className="btn ghost sm"
                  onClick={() => void automation.refetch()}
                >{tx("Retry integration status")}</button>
              )}
              <p className="muted">{tx("Reminders use the stream date saved in Prepare → Details. Only approved registrants who consented are included. Follow-up applies to broadcasts ending after this schedule is enabled. Delivery runs in batches and may occur shortly after the selected time.")}</p>
              <label>{ptx("Subject{v0}", {v0: (<input
                  className="input"
                  value={p.followup.subject}
                  onChange={(e) =>
                    change({
                      ...p,
                      followup: { ...p.followup, subject: e.target.value },
                    })
                  }
                />)})}</label>
              <label>{ptx("Message{v0}", {v0: (<textarea
                  className="input"
                  rows={4}
                  value={p.followup.message}
                  onChange={(e) =>
                    change({
                      ...p,
                      followup: { ...p.followup, message: e.target.value },
                    })
                  }
                />)})}</label>
              <p className="muted">{tx("Results → Audience report lets you export the message with a selected recipient segment. When enabled, registration, attendance, quiz, booking and completion events trigger your configured live.* webhook subscriptions. No email is sent directly from this form.")}</p>
            </details>
          )}
        </fieldset>
        {mode === "control" && p.features.timeline && (
          <form
            className="program-row"
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              try {
                await api.post(prefix + "/chapters", { title: chapter });
                setChapter("");
                setNotice("Chapter marked");
              } catch (e) {
                setNotice(failure(e));
              } finally {
                setBusy(false);
              }
            }}
          >
            <label>{ptx("Replay chapter{v0}", {v0: (<input
                className="input"
                value={chapter}
                onChange={(e) => setChapter(e.target.value)}
                placeholder={tx("e.g. Product demonstration")}
                maxLength={180}
              />)})}</label>
            <button
              className="btn outline"
              disabled={disabled || stream.status !== "LIVE" || !chapter.trim()}
            >{tx("Mark now")}</button>
          </form>
        )}
        {!Object.values(p.features).some(Boolean) && mode === "control" && (
          <p className="muted">{tx("Enable tools in Prepare → Event tools.")}</p>
        )}
        <div className="program-save">
          <button
            className="btn primary"
            disabled={disabled || !dirty}
            onClick={() => void save()}
          >
            {busy ? tx("Saving…") : tx("Save event tools")}
          </button>
          {dirty && (
            <button
              className="btn ghost"
              disabled={busy}
              onClick={() => {
                if (window.confirm("Discard unsaved tool changes?")) {
                  setDirty(false);
                  void query.refetch();
                }
              }}
            >{tx("Reload saved")}</button>
          )}
          <span role="status">{notice}</span>
        </div>
      </details>
    </section>
  );
}
